import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../../../connection/socket';
import { GreenroomState as GS } from '../../../../stores/types/types';
import { Invitation } from '../../../../stores/reducers/invitations';
import { ENTRY_TYPE, ENTRY_OPTIONS, ROOM_STATES } from '../../../../stores/reducers/settings';
import { BROADCAST_STATES } from '../../../../stores/reducers/broadcasting';
import { MODAL_TEMPLATES } from '../../../../stores/reducers/modal';
import * as SettingsActions from '../../../../stores/actions/settings';
import * as ModalActions from '../../../../stores/actions/modal';
import * as BroadcastActions from '../../../../stores/actions/broadcasting';
import Icon, { ICONS, COLORS } from '../../../general-components/icon';
import SelectSmall from '../../../general-components/select-small';
import Switch from '../../../general-components/switch';
import Popover from '../../../general-components/popover';
import StreamingDestinations from '../../../general-components/streaming-destinations';
import * as Alerts from '../../../general-components/alert-service';
import StreamingSettings, { StreamSettingLocation } from '../../../right-panel/settings/streaming-settings';
import { SelectGroupOption } from '../../../general-components/select-group';
import InfoBadge, { InfoBadgeMsg } from '../../../general-components/tooltips/info-badge';

interface Props {
	socket: Socket | null;
	homeMeeting: any;
	invitations: Invitation[];
}

export default function ShowDetailsSettings(props: Props) {
	const askToJoin = useSelector((s: GS) => s.SettingsReducer.askToJoin);
	const useShowboard = useSelector((s: GS) => s.SettingsReducer.useShowboard);
	const feedScaling = useSelector((s: GS) => s.SettingsReducer.feedScaling);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const entryWithoutProducer = useSelector((s: GS) => s.SettingsReducer.entryWithoutProducer);
	const useModeratedQuestions = useSelector((s: GS) => s.SettingsReducer.useModeratedQuestions);
	const backstageCommsActive = useSelector((s: GS) => s.SettingsReducer.backstageCommsActive);
	const useEmailInvites = useSelector((s: GS) => s.SettingsReducer.useEmailInvites);
	const goLiveButtonClicked = useSelector((s: GS) => s.BroadcastReducer.goLiveButtonClicked);
	const presenterAudienceTab = useSelector((s: GS) => s.SettingsReducer.presenterAudienceTab);
	const { audience_tab: audienceTab } = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const dispatch = useDispatch();
	const { socket, homeMeeting, invitations } = props;
	const [addingReply, setAddingReply] = useState<boolean>(false);
	const [destinationPopoverOpen, setDestinationPopoverOpen] = useState<boolean>(false);

	function handleClick(e: any) {
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (path[i].className?.includes) {
				if ((typeof path[i].className === 'string'
				&& path[i].className.includes('message-reply-input'))) {
					keepOpen = true;
					break;
				}
			}
		}

		if (!keepOpen) {
			setAddingReply(false);
		}
	}

	function selectedEntryOptions() {
		if (entryWithoutProducer === true && askToJoin === false) return ENTRY_OPTIONS[0];
		if (entryWithoutProducer === true && askToJoin === true) return ENTRY_OPTIONS[1];
		if (entryWithoutProducer === false && askToJoin === false) return ENTRY_OPTIONS[2];
		return ENTRY_OPTIONS[3];
	}

	function changeEntryWithoutProducer() {
		dispatch(SettingsActions.setEntryWithoutProducer(!entryWithoutProducer));
		invitations.forEach((invitation: Invitation) => {
			socket?.sendMessage('update-entry', {
				meeting: homeMeeting!.meeting,
				entry_without_producer: !entryWithoutProducer,
			}, `temp-${invitation.profile.email}`);
		});
	}

	function changeEntryOptions(val: number) {
		switch (val) {
			case ENTRY_TYPE.NO_REQUIREMENTS:
				if (!entryWithoutProducer) changeEntryWithoutProducer();
				if (askToJoin) dispatch(SettingsActions.setAskToJoin(!askToJoin));
				break;
			case ENTRY_TYPE.REQUIRE_APPROVAL:
				if (!entryWithoutProducer) changeEntryWithoutProducer();
				if (!askToJoin) dispatch(SettingsActions.setAskToJoin(!askToJoin));
				break;
			case ENTRY_TYPE.REQUIRE_PRESENCE:
				if (entryWithoutProducer) changeEntryWithoutProducer();
				if (askToJoin) dispatch(SettingsActions.setAskToJoin(!askToJoin));
				break;
			case ENTRY_TYPE.REQUIRE_APPROVAL_PRESENCE:
				if (entryWithoutProducer) changeEntryWithoutProducer();
				if (!askToJoin) dispatch(SettingsActions.setAskToJoin(!askToJoin));
				break;
			default:
				break;
		}
	}

	function changeUseShowboard() {
		if (roomState === ROOM_STATES.EDITING && useShowboard) {
			return Alerts.showAlert({
				message: 'Please exit showbuilder to turn off showboard.',
				duration: 2000,
				type: 'neutral',
			});
		}
		return dispatch(SettingsActions.setUseShowboard(!useShowboard));
	}

	function addDestination(newDestination: SelectGroupOption) {
		dispatch(ModalActions.setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.STREAM_DESTINATION_SETTINGS,
			modalData: {
				destinationType: newDestination,
			},
		}));

		setDestinationPopoverOpen(false);
	}

	useEffect(() => {
		if (addingReply) {
			setTimeout(() => {
				window.addEventListener('click', handleClick);
			});
		}

		return () => {
			window.removeEventListener('click', handleClick);
		};
	}, [addingReply]);

	useEffect(() => {
		const onStates = [
			BROADCAST_STATES.BROADCAST_STARTED,
			BROADCAST_STATES.BROADCAST_STARTING,
			BROADCAST_STATES.BROADCAST_PREWARMING,
		];
		const offStates = [
			BROADCAST_STATES.OFF,
			BROADCAST_STATES.READY,
			BROADCAST_STATES.BROADCAST_STOPPED,
			BROADCAST_STATES.BROADCAST_PREWARM_FAILED,
			BROADCAST_STATES.BROADCAST_FAILED,
			BROADCAST_STATES.DRAINING,
			BROADCAST_STATES.KILLED,
		];
		if (onStates.includes(serverState) && !goLiveButtonClicked) {
			dispatch(BroadcastActions.setGoLiveButtonClicked(true));
		}
		if (offStates.includes(serverState) && goLiveButtonClicked) {
			dispatch(BroadcastActions.setGoLiveButtonClicked(false));
		}
	}, [serverState]);

	return (
		<>
			<div className="show-details-settings">
				<div className="section-header  position-relative">
					<label>Streaming</label>
					<div className="section-buttons">
						<button onClick={() => setDestinationPopoverOpen(true)} className='transparent'>
							<Icon name={ICONS.ADD} color={COLORS.PALE_GREEN} size={14} className='margin-right-small' />
							<span className='text text-green'>Add Destination</span>
						</button>
						<Popover
							open={destinationPopoverOpen}
							close={() => setDestinationPopoverOpen(false)}
							className='destination-options'
						>
							<StreamingDestinations buttonCallback={addDestination} buttonClassNames='option'></StreamingDestinations>
						</Popover>
					</div>
				</div>
				<div className="streaming-container">
					<StreamingSettings
						socket={socket}
						meeting={homeMeeting}
						location={StreamSettingLocation.ShowDetails}
					/>
				</div>
				<div className="section-header advanced">
					<label>Advanced</label>
				</div>
				<div className="advanced-settings-container padding-small background-medium">
					<div className='align-items-center justify-content-space-between margin-small'>
						<span>Entry Requirements</span>
						<SelectSmall
							options={ENTRY_OPTIONS}
							onChange={changeEntryOptions}
							label={ENTRY_OPTIONS[0].label}
							selected={selectedEntryOptions() || ENTRY_OPTIONS[0]}
							dropdown={true}
						/>
					</div>
					<div className='align-items-center justify-content-space-between padding-small'>
						<span>Email Invites</span>
						<Switch
							on={useEmailInvites}
							onChange={() => dispatch(SettingsActions.setUseEmailInvites(!useEmailInvites))}
							className={'green'}
						/>
					</div>
					<div className='align-items-center justify-content-space-between padding-small'>
						<span>Brandlive Q&A</span>
						<Switch
							on={useModeratedQuestions}
							onChange={() => dispatch(
								SettingsActions.setModeratedQuestions(!useModeratedQuestions),
							)}
							className={'green'}
						/>
					</div>
					<div className='align-items-center justify-content-space-between padding-small'>
						<div className="flex-align-center">
							<label className='margin-right-small'>Video Feed Management</label>
							<InfoBadge message={InfoBadgeMsg.VideoResolution} />
						</div>
						<Switch
							on={feedScaling}
							onChange={() => dispatch(SettingsActions.setFeedScaling(!feedScaling))}
							className={'green'}
						/>
					</div>
					<div className='align-items-center justify-content-space-between padding-small'>
						<div className="flex-align-center">
							<label className='margin-right-small'>Backstage & Producer comms</label>
							<InfoBadge message={InfoBadgeMsg.BackstageComms}/>
						</div>
						<Switch
							on={backstageCommsActive}
							onChange={() => dispatch(
								SettingsActions.setBackstageCommsActive(!backstageCommsActive),
							)}
							className={'green'}
						/>
					</div>
					<div className='align-items-center justify-content-space-between padding-small'>
						<span>Showboard</span>
						<Switch
							on={useShowboard}
							onChange={changeUseShowboard}
							className={'green'}
						/>
					</div>
					{audienceTab && (
						<div className="align-items-center justify-content-space-between padding-small">
							<div className="flex-align-center">
								<label className='margin-right-small'>Show audience tab to presenters</label>
								<InfoBadge message={InfoBadgeMsg.AudienceTabSetting} />
							</div>
							<Switch
								on={presenterAudienceTab}
								onChange={() => dispatch(
									SettingsActions.setPresenterAudienceTab(!presenterAudienceTab),
								)}
								className='green'
							/>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
